import { useOidc } from '@/auth/OidcProvider'
import { useState, useRef, useEffect, Suspense } from 'react'
import { BetaFeedbackModal } from '@/shell/BetaFeedbackModal'
import { useComingSoon } from '@/shell/useComingSoon'
import { DropdownButton } from '@/shell/components/DropdownButton'
import { styles } from '@/shell/layout.styles'
import { AccessTokenModal, SupportModal } from '@/shell/lazyModals'

export function ProfileMenu() {
  const { user, logout } = useOidc()
  const [open, setOpen] = useState(false)
  const [showTokens, setShowTokens] = useState(false)
  const [showFeedback, setShowFeedback] = useState(false)
  const [showSupport, setShowSupport] = useState(false)
  const [hovered, setHovered] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const notifications = useComingSoon({
    feature: 'notification-settings',
    track: 'notifications_coming_soon',
    sessionKey: 'interest_notifications',
    title: 'Notifications',
    description: "Notification settings will let you subscribe more granularly to scan, build results, policy failures, and workspace events. We'll announce it when it's ready.",
  })

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <>
      {showTokens && (
        <Suspense fallback={null}>
          <AccessTokenModal onClose={() => setShowTokens(false)} />
        </Suspense>
      )}
      {showFeedback && <BetaFeedbackModal onClose={() => setShowFeedback(false)} />}
      {showSupport && (
        <Suspense fallback={null}>
          <SupportModal onClose={() => setShowSupport(false)} />
        </Suspense>
      )}
      {notifications.modal}
      <div ref={ref} style={{ position: 'relative' }}>
        <button
          style={{ ...styles.avatarBtn, ...(hovered ? styles.avatarBtnHover : {}) }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={() => setOpen((o) => !o)}
        >
          <div style={styles.userAvatar}>{user?.profile.name?.[0]?.toUpperCase() ?? '?'}</div>
          <span style={styles.avatarName}>{user?.profile.name ?? user?.profile.email ?? 'Account'}</span>
          <span style={styles.avatarChevron}>▾</span>
        </button>
        {open && (
          <div style={styles.dropdown}>
            <div style={styles.dropdownHeader}>
              <div style={styles.dropdownName}>{user?.profile.name ?? user?.profile.email ?? 'User'}</div>
              <div style={styles.dropdownEmail}>{user?.profile.email}</div>
            </div>
            <div style={styles.dropdownDivider} />
            <DropdownButton onClick={() => { setOpen(false); setShowTokens(true) }}>
              Personal Access Token
            </DropdownButton>
            <DropdownButton onClick={() => { setOpen(false); notifications.openModal() }}>
              Notifications
              <span style={styles.soonBadge}>Soon</span>
            </DropdownButton>
            <DropdownButton onClick={() => { setOpen(false); setShowFeedback(true) }}>
              Send feedback
            </DropdownButton>
            <div style={styles.dropdownDivider} />
            <div style={styles.dropdownSection}>
              <div style={styles.dropdownSectionLabel}>Help</div>
              <DropdownButton onClick={() => { setOpen(false); setShowSupport(true) }}>
                Support
              </DropdownButton>
              <a style={styles.dropdownLink} href="https://developer.hashicorp.com/terraform/language/modules/sources#terraform-registry" target="_blank" rel="noreferrer">
                Terraform registry docs ↗
              </a>
              <a style={styles.dropdownLink} href="https://developer.hashicorp.com/terraform/cli/config/config-file#credentials" target="_blank" rel="noreferrer">
                Configure credentials ↗
              </a>
              <a style={styles.dropdownLink} href="https://github.com/terramantle" target="_blank" rel="noreferrer">
                GitHub ↗
              </a>
            </div>
            <div style={styles.dropdownDivider} />
            <DropdownButton onClick={() => logout()}>
              Sign out
            </DropdownButton>
          </div>
        )}
      </div>
    </>
  )
}
